/**
 * Dropdown menu — row actions and the account menu.
 *
 * Radix handles the typeahead, roving focus and `aria-*` a real menu needs, and
 * `dir` comes from the direction provider so submenus open towards the inline
 * start in Arabic without any per-menu configuration.
 */
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'

import { useLocale } from '@/shared/i18n/useLocale'

import { cn } from './cn'

/** Pass-throughs — see the note in Dialog.tsx on why these are not re-exports. */
export function DropdownMenuTrigger(props: React.ComponentProps<typeof MenuPrimitive.Trigger>) {
  return <MenuPrimitive.Trigger {...props} />
}

export function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Separator>) {
  return <MenuPrimitive.Separator className={cn('my-1 h-px bg-line', className)} {...props} />
}

/**
 * Wrapped rather than re-exported so `dir` is set once, here.
 *
 * Radix mirrors `align` and submenu placement from the root's `dir`. Without it
 * an Arabic menu opens off the wrong edge of its trigger, and each caller would
 * have to remember to pass it.
 */
export function DropdownMenuRoot(props: React.ComponentPropsWithoutRef<typeof MenuPrimitive.Root>) {
  const { direction } = useLocale()
  return <MenuPrimitive.Root dir={direction} {...props} />
}

export function DropdownMenuContent({
  className,
  children,
  align = 'end',
}: {
  className?: string | undefined
  children: React.ReactNode
  align?: 'start' | 'center' | 'end' | undefined
}) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 min-w-44 overflow-hidden rounded-lg border border-line-strong bg-raised p-1 shadow-lg',
          // A floating layer must read as floating on **both** themes. In dark
          // mode `bg-raised` sits only 0.08 lightness above the page and
          // Tailwind's shadow is black on near-black — so an open menu had no
          // separation at all and the heading behind it read straight through.
          // `ring-edge` flips with the theme and carries what the shadow cannot.
          // (Not a `dark:` variant: components carry none — the palette's
          // `[data-theme='dark']` block re-points the token instead.)
          'ring-1 ring-edge',
          className,
        )}
      >
        {children}
      </MenuPrimitive.Content>
    </MenuPrimitive.Portal>
  )
}

export type DropdownMenuItemProps = React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item> & {
  tone?: 'default' | 'danger'
}

export function DropdownMenuItem({ className, tone = 'default', ...props }: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm outline-none',
        'data-highlighted:bg-sunken',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        tone === 'danger' && 'text-danger data-highlighted:bg-danger-soft',
        className,
      )}
      {...props}
    />
  )
}

/**
 * A menu row that is on or off — the account menu's dark-mode switch.
 *
 * Radix's `CheckboxItem`, so the row is a `menuitemcheckbox` with
 * `aria-checked`: the menu equivalent of a pressed toggle button. (`aria-pressed`
 * is not valid on a menu item, so the footer's button and this row express the
 * same state through the attribute each role allows.)
 *
 * ⚠️ The check is the visible state, so it is a shape, not a colour — and its
 * slot is reserved when unchecked, so the label does not jump sideways as the
 * row toggles under the pointer.
 */
export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>) {
  return (
    <MenuPrimitive.CheckboxItem
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm outline-none',
        'data-highlighted:bg-sunken',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    >
      {children}
      <span className="ms-auto flex size-4 items-center justify-center" aria-hidden="true">
        <MenuPrimitive.ItemIndicator>
          <Check className="text-brand-fg" />
        </MenuPrimitive.ItemIndicator>
      </span>
    </MenuPrimitive.CheckboxItem>
  )
}
